import React, { Fragment, useRef, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItems,
  Portal,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { CrossIcon, TrashErrorIcon } from '../../assets/icons';
import ActionButton from '../ActionButton';
import IconButton from '../IconButton';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'free' | string;
  showDeleteButton?: boolean;
  disableDeleteButton?: boolean;
  onDelete?: () => void;
  deleteLoading?: boolean;
  className?: string;
  /** Optional stacking layer for a dialog that must sit above page controls. */
  dialogLayerClassName?: string;
  /** Slides in from the right at full height instead of sitting centred. */
  isDrawer?: boolean;
  /**
   * `inset-card` paints the drawer as a gray panel with a floating white card,
   * which is the canonical DHS chrome. `default` leaves the body plain white.
   */
  drawerChrome?: 'default' | 'inset-card';
  /**
   * Same choice for the centred dialog. `inset-card` gives it the canonical
   * chrome: solid gray panel, gray header with no border, and a floating white
   * card holding the content. `default` keeps the older flat white dialog.
   */
  chrome?: 'default' | 'inset-card';
  /**
   * Action bar for the centred dialog. Like `drawerFooter`, it renders as a
   * sibling of the white card so the card keeps its bottom corners.
   */
  footer?: React.ReactNode;
  /**
   * Action bar for a drawer. It renders as a sibling of the white card, on the
   * gray shell — never as a child. The card is overflow-hidden, so a footer
   * passed through `children` gets squared off against it and the bottom
   * corners lose their radius. No `border-t` here: the gray already separates.
   */
  drawerFooter?: React.ReactNode;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showDeleteButton = false,
  disableDeleteButton = false,
  onDelete,
  deleteLoading,
  className = '',
  dialogLayerClassName = 'z-50',
  isDrawer = false,
  drawerChrome = 'default',
  drawerFooter,
  chrome = 'default',
  footer,
}: ModalProps) {
  const { t } = useTranslation();
  const cancelButtonRef = useRef(null);
  const usesInsetCard = isDrawer && drawerChrome === 'inset-card';
  const centredInsetCard = !isDrawer && chrome === 'inset-card';
  const namedSizeClasses: Record<string, string> = {
    sm: 'sm:max-w-sm w-full',
    md: 'sm:max-w-md w-full',
    lg: 'sm:max-w-lg w-full',
    xl: 'sm:max-w-xl w-full',
    full: 'sm:max-w-full sm:w-full w-full',
    free: 'sm:max-w-full sm:w-auto',
  };
  /*
   * In the legacy literal the computed `[size]` key came last, so passing a named
   * size overwrote it with a junk class (`w-[lg]`) and every dialog fell back to
   * the hardcoded 600px. The canonical panel picks its width here instead; the
   * legacy branch below keeps the old object so its widths do not shift.
   */
  const insetSizeClass = namedSizeClasses[size] ?? `w-[${size}]`;
  const sizeClasses = {
    ...namedSizeClasses,
    [size]: `w-[${size}]`,
  };

  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const handleMenuButtonClick = (e: React.MouseEvent) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    setMenuPosition({
      x: rect.right,
      y: rect.bottom,
    });
  };

  if (isDrawer) {
    return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          initialFocus={cancelButtonRef}
          onClose={() => {}}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-hidden">
            <div className="flex h-full items-stretch justify-end">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel
                  // Full-bleed sheet on phones, 560px panel from sm up. The safe-area
                  // padding keeps the footer clear of the home indicator on iOS.
                  className={`relative flex h-full w-full flex-col bg-ds-bg-secondary shadow-xl pb-[env(safe-area-inset-bottom)] sm:max-w-[560px] ${className}`}
                >
                  {/* Gray header, no border-b: the panel colour already separates it */}
                  {title && (
                    <div className="flex h-14 flex-shrink-0 items-center justify-between px-5">
                      <DialogTitle className="text-base font-semibold tracking-tight text-ds-text-primary">
                        {title}
                      </DialogTitle>
                      {onClose && (
                        <IconButton disabled={!!deleteLoading} icon={CrossIcon} onClick={onClose} />
                      )}
                    </div>
                  )}

                  {/* No px-*: the white card reaches edge to edge, the corners do the floating */}
                  <div
                    className={`flex min-h-0 flex-1 flex-col overflow-x-hidden ${
                      drawerFooter ? 'pb-0' : 'pb-4'
                    }`}
                  >
                    {usesInsetCard ? (
                      <div
                        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-ds-border bg-white"
                        data-testid="modal-inset-card"
                      >
                        {children}
                      </div>
                    ) : (
                      children
                    )}
                  </div>

                  {drawerFooter}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  if (centredInsetCard) {
    return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className={`relative ${dialogLayerClassName}`}
          initialFocus={cancelButtonRef}
          onClose={() => {}}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </TransitionChild>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel
                  className={`relative flex max-h-[calc(100vh-4rem)] max-w-[calc(100vw-2rem)] transform flex-col overflow-hidden rounded-2xl bg-ds-bg-secondary text-left shadow-xl transition-all sm:my-8 ${insetSizeClass} ${className}`}
                >
                  {/* Gray header, no border-b: the panel colour already separates it */}
                  <div className="flex h-14 flex-shrink-0 items-center justify-between px-5">
                    <DialogTitle className="min-w-0 truncate text-base font-semibold tracking-tight text-ds-text-primary">
                      {title}
                    </DialogTitle>
                    {onClose && (
                      <IconButton disabled={!!deleteLoading} icon={CrossIcon} onClick={onClose} />
                    )}
                  </div>

                  {/* No px-*: the white card reaches edge to edge, its corners do the floating */}
                  <div
                    className={`flex min-h-0 flex-1 flex-col ${footer ? 'pb-0' : 'pb-4'}`}
                    data-testid="modal-inset-card"
                  >
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-ds-border bg-white">
                      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
                    </div>
                  </div>

                  {footer && (
                    <div className="flex h-14 flex-shrink-0 items-center justify-end gap-3 px-5">
                      {footer}
                    </div>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={`relative transform overflow-hidden rounded-[10px] bg-white text-left shadow-xl transition-all sm:my-8 ${sizeClasses[size]} w-[600px] max-w-[90vw] ${className} flex flex-col border border-ds-border`}
              >
                {title && (
                  <div className="px-4 py-3 flex justify-between items-center border-b border-ds-border-light flex-shrink-0">
                    <DialogTitle className="text-sm font-medium leading-6 text-ds-text-primary tracking-[0.02em]">
                      {title.slice(0, 35)}
                      {title.length > 35 && '...'}
                    </DialogTitle>
                    <div className="flex gap-2">
                      {showDeleteButton && onDelete && (
                        <Menu as="div" className={`relative flex-shrink-0 border-0 mr-1`}>
                          {({ open, close }) => (
                            <>
                              <MenuButton
                                disabled={deleteLoading || disableDeleteButton}
                                className="relative group cursor-pointer disabled:cursor-not-allowed"
                                onClick={handleMenuButtonClick}
                              >
                                <div
                                  className={`flex items-center gap-2 px-2 py-1 text-sm font-medium text-error ${
                                    disableDeleteButton ? 'opacity-80' : ''
                                  }`}
                                >
                                  <img
                                    src={TrashErrorIcon}
                                    alt=""
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                  />
                                  {deleteLoading ? `${t('deleting')}...` : t('delete')}
                                </div>
                                {disableDeleteButton && (
                                  <div className="absolute z-10 hidden group-hover:block bg-black/70 text-white text-xs rounded py-1 px-2 top-0 right-20 whitespace-nowrap">
                                    {t('deleteInUseProduct')}
                                  </div>
                                )}
                              </MenuButton>

                              <Portal>
                                <Transition
                                  show={open}
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <MenuItems
                                    static
                                    className="fixed z-[9999] h-20 px-3 pt-2 pb-3 flex flex-col justify-between origin-top-right rounded-ds-default bg-ds-text-primary shadow-ds-card focus:outline-none"
                                    style={{
                                      top: menuPosition?.y ?? 0,
                                      left: menuPosition?.x ?? 0,
                                      transform: 'translateX(-100%)',
                                    }}
                                  >
                                    <span className="text-sm font-medium text-white">
                                      {t('areYouSure')}
                                    </span>
                                    <div className="flex justify-between gap-2">
                                      <ActionButton
                                        disabled={!!deleteLoading}
                                        onClick={onDelete}
                                        label={deleteLoading ? t('deleting') : t('delete')}
                                        variant="outlined"
                                        color="white"
                                        size="sm"
                                      />
                                      <ActionButton
                                        disabled={!!deleteLoading}
                                        onClick={close}
                                        label={t('cancel')}
                                        variant="outlined"
                                        color="white"
                                        size="sm"
                                      />
                                    </div>
                                  </MenuItems>
                                </Transition>
                              </Portal>
                            </>
                          )}
                        </Menu>
                      )}
                      {onClose && (
                        <IconButton disabled={!!deleteLoading} icon={CrossIcon} onClick={onClose} />
                      )}
                    </div>
                  </div>
                )}
                {/* bg-inherit, not bg-white: a panel overridden to gray (the
                    confirmation dialog) would otherwise get a white body back */}
                <div className="bg-inherit flex-1 overflow-y-auto">{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
